export interface NaturalFractionNotation {
  source: string;
  canonical: string;
  value: number;
}

const INTEGER_TOKEN = String.raw`(?:[0-9０-９]+|[零〇一二两三四五六七八九十百千万]+)`;
const NATURAL_FRACTION = new RegExp(
  `([负+＋\\-−－]?)\\s*(${INTEGER_TOKEN})\\s*分之\\s*([负+＋\\-−－]?)\\s*(${INTEGER_TOKEN})`,
  "gu",
);

export function naturalFractionNotations(
  value: string,
): NaturalFractionNotation[] {
  return [...value.matchAll(NATURAL_FRACTION)].flatMap((match) => {
    const notation = buildNotation(
      match[0],
      match[1] ?? "",
      match[2] ?? "",
      match[3] ?? "",
      match[4] ?? "",
    );
    return notation === undefined ? [] : [notation];
  });
}

export function normalizeNaturalFractionWording(value: string): string {
  return value.replace(
    NATURAL_FRACTION,
    (source, wholeSign, denominator, numeratorSign, numerator) =>
      buildNotation(
        source,
        wholeSign,
        denominator,
        numeratorSign,
        numerator,
      )?.canonical ?? source,
  );
}

export function parseNaturalFractionLiteral(
  value: string,
): number | undefined {
  const normalized = value.trim();
  const notations = naturalFractionNotations(normalized);
  if (notations.length !== 1 || notations[0]?.source !== normalized) {
    return undefined;
  }
  return notations[0].value;
}

function buildNotation(
  source: string,
  wholeSignText: string,
  denominatorText: string,
  numeratorSignText: string,
  numeratorText: string,
): NaturalFractionNotation | undefined {
  const denominator = parseNaturalInteger(denominatorText);
  const numerator = parseNaturalInteger(numeratorText);
  if (
    denominator === undefined ||
    denominator === 0 ||
    numerator === undefined
  ) {
    return undefined;
  }
  const signedNumerator =
    numerator * signValue(wholeSignText) * signValue(numeratorSignText);
  return {
    source,
    canonical: canonicalFraction(signedNumerator, denominator),
    value: signedNumerator / denominator,
  };
}

function parseNaturalInteger(value: string): number | undefined {
  const normalized = value.normalize("NFKC");
  if (/^\d+$/u.test(normalized)) {
    const parsed = Number(normalized);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
  }
  if (!/^[零〇一二两三四五六七八九十百千万]+$/u.test(normalized)) {
    return undefined;
  }
  if (!/[十百千万]/u.test(normalized)) {
    const digits = [...normalized].map((character) => CHINESE_DIGITS[character]);
    return digits.some((digit) => digit === undefined)
      ? undefined
      : Number(digits.join(""));
  }

  let total = 0;
  let section = 0;
  let current = 0;
  for (const character of normalized) {
    const digit = CHINESE_DIGITS[character];
    if (digit !== undefined) {
      current = digit;
      continue;
    }
    if (character === "万") {
      section += current;
      total += (section || 1) * 10_000;
      section = 0;
      current = 0;
      continue;
    }
    const unit = SMALL_UNITS[character];
    if (unit === undefined) return undefined;
    section += (current || 1) * unit;
    current = 0;
  }
  const result = total + section + current;
  return Number.isSafeInteger(result) ? result : undefined;
}

function canonicalFraction(numerator: number, denominator: number): string {
  const divisor = greatestCommonDivisor(Math.abs(numerator), denominator);
  const reducedNumerator = numerator / divisor;
  const reducedDenominator = denominator / divisor;
  return reducedDenominator === 1
    ? String(reducedNumerator)
    : `${reducedNumerator}/${reducedDenominator}`;
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = left;
  let b = right;
  while (b !== 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a || 1;
}

function signValue(value: string): 1 | -1 {
  const normalized = value.normalize("NFKC");
  return normalized === "负" || normalized === "-" ? -1 : 1;
}

const CHINESE_DIGITS: Readonly<Record<string, number>> = {
  零: 0,
  〇: 0,
  一: 1,
  二: 2,
  两: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

const SMALL_UNITS: Readonly<Record<string, number>> = {
  十: 10,
  百: 100,
  千: 1_000,
};
